import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Phone, MapPin } from "lucide-react";
import { CITY_INDEX, CITY_PAGES } from "@/data/cities";
import { SERVICES_INDEX, SERVICES } from "@/data/services";
import { SectionHeader, Reveal } from "@/components/SectionHeader";
import SEO, { breadcrumbJsonLd, serviceJsonLd } from "@/components/SEO";

export default function CityService() {
  const { city, service } = useParams();
  const c = CITY_INDEX[city];
  const s = SERVICES_INDEX[service];

  if (!c || !s) {
    return (
      <div className="container-x py-32 text-center">
        <h1 className="font-display text-3xl font-bold">Page not found</h1>
        <Link to="/locations" className="btn-primary mt-8 inline-flex">Texas locations</Link>
      </div>
    );
  }

  const Icon = s.icon;
  const nearby = CITY_PAGES.filter((x) => x.region === c.region && x.slug !== c.slug).slice(0, 6);
  const relatedSvcs = SERVICES.filter((x) => x.slug !== s.slug).slice(0, 3);

  return (
    <div data-testid={`city-service-${c.slug}-${s.slug}`}>
      <SEO
        title={`${s.title} in ${c.name}, TX — Independent Assessment`}
        description={`${s.summary} Hurtler Environmental serves ${c.name}, ${c.county} and the ${c.region}.`}
        path={`/locations/${c.slug}/${s.slug}`}
        jsonLd={[
          serviceJsonLd({ name: `${s.title} in ${c.name}, TX`, description: s.summary, slug: s.slug }),
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Texas Locations", path: "/locations" },
            { name: c.name, path: `/locations/${c.slug}` },
            { name: s.title, path: `/locations/${c.slug}/${s.slug}` },
          ]),
        ]}
      />
      <section className="bg-[#0F172A] text-white py-20 md:py-24">
        <div className="container-x">
          <Link to={`/locations/${c.slug}`} className="text-white/60 hover:text-white text-sm inline-flex items-center gap-2 mb-8">
            <ArrowLeft className="w-4 h-4" /> All services in {c.name}
          </Link>
          <div className="flex items-start gap-6">
            <div className="w-16 h-16 rounded-2xl bg-[#7C3AED]/20 grid place-items-center shrink-0">
              <Icon className="w-7 h-7 text-[#F59E0B]" />
            </div>
            <div>
              <div className="overline text-[#F59E0B] mb-3">
                {c.county} • {c.region}
              </div>
              <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight leading-tight">
                {s.title} in <span className="text-[#F59E0B]">{c.name}, Texas</span>
              </h1>
              <p className="mt-5 text-lg text-white/70 max-w-2xl leading-relaxed">{s.summary}</p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/schedule" data-testid={`cs-schedule-${c.slug}-${s.slug}`} className="btn-primary">
                  Schedule in {c.name} <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
                <a href="tel:9562172604" className="btn-outline-dark">
                  <Phone className="w-4 h-4 mr-2" /> 956.217.2604
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-pad bg-white">
        <div className="container-x grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-12">
            <div>
              <div className="overline mb-3">Why {c.name} chooses Hurtler for {s.title}</div>
              <p className="text-lg text-slate-700 leading-relaxed">
                {s.why} As an independent firm serving {c.county} and the broader {c.region},
                Hurtler performs {s.title.toLowerCase()} without any structural incentive to expand
                scope or recommend remediation — protecting {c.name} property owners,
                attorneys, and carriers with defensible findings.
              </p>
            </div>
            {[
              { h: "When is it needed?", b: s.when },
              { h: `How we perform ${s.title} in ${c.name}`, b: s.how },
              { h: "Laboratory methods", b: s.lab },
              { h: "Interpretation", b: s.interpretation },
            ].map((blk) => (
              <div key={blk.h}>
                <div className="overline mb-3">{blk.h}</div>
                <p className="text-lg text-slate-700 leading-relaxed">{blk.b}</p>
              </div>
            ))}
            <div>
              <div className="overline mb-3">Who needs this service in {c.name}?</div>
              <p className="text-lg text-slate-700 leading-relaxed">{s.who}</p>
            </div>
          </div>

          <aside className="space-y-8">
            <div className="card-soft p-7">
              <div className="overline mb-4">Equipment used</div>
              <ul className="space-y-3">
                {s.equipment.map((e) => (
                  <li key={e} className="flex items-start gap-3 text-sm text-slate-700">
                    <Check className="w-4 h-4 text-[#7C3AED] mt-0.5" /> {e}
                  </li>
                ))}
              </ul>
            </div>
            <div className="card-soft p-7">
              <div className="overline mb-4">Standards applied</div>
              <ul className="space-y-3">
                {s.standards.map((std) => (
                  <li key={std} className="text-sm text-slate-700">{std}</li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl bg-[#0F172A] text-white p-7">
              <div className="font-display font-bold text-lg mb-2">Schedule in {c.name}</div>
              <p className="text-white/70 text-sm mb-5">Most assessments scheduled within 48–72 hours.</p>
              <Link to="/schedule" className="btn-primary w-full justify-center">
                Schedule <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </div>
          </aside>
        </div>
      </section>

      {/* Nearby cities cross-link */}
      {nearby.length > 0 && (
        <section className="section-pad bg-[#F8FAFC]">
          <div className="container-x">
            <div className="overline mb-4">{s.title} also available in</div>
            <h2 className="font-display text-3xl font-bold text-[#0F172A] mb-10">Nearby {c.region} cities</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {nearby.map((n) => (
                <Link
                  key={n.slug}
                  to={`/locations/${n.slug}/${s.slug}`}
                  data-testid={`cs-nearby-${n.slug}`}
                  className="card-soft p-4 text-center"
                >
                  <MapPin className="w-4 h-4 text-[#7C3AED] mx-auto mb-2" />
                  <div className="font-display font-bold text-sm text-[#0F172A]">{n.name}</div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related services in this city */}
      <section className="section-pad bg-white">
        <div className="container-x">
          <div className="overline mb-4">Other services in {c.name}</div>
          <h2 className="font-display text-3xl font-bold text-[#0F172A] mb-10">Related Hurtler services</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {relatedSvcs.map((r) => {
              const RIcon = r.icon;
              return (
                <Link
                  key={r.slug}
                  to={`/locations/${c.slug}/${r.slug}`}
                  data-testid={`cs-related-${r.slug}`}
                  className="card-soft block p-7"
                >
                  <RIcon className="w-6 h-6 text-[#7C3AED] mb-4" />
                  <div className="font-display font-bold text-[#0F172A] mb-2">
                    {r.title} in {c.name}
                  </div>
                  <div className="text-sm text-slate-600 leading-relaxed">{r.summary}</div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
